import React, { useEffect, useRef, useState } from 'react';

import ChevronDownIcon from 'icons/chevron-down.svg';
import ChevronUpIcon from 'icons/chevron-up.svg';
import CloseIcon from 'icons/ic-close.svg';
import _ from 'lodash';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import voca from 'voca';

import OutlinedButton from 'components/application/buttons/OutlinedButton';
import OptionChoice from 'components/application/OptionChoice';
import Options from 'components/application/Options';
import SidePanel from 'components/application/SidePanel';
import Tooltip from 'components/application/Tooltip';

import { useCurrentActor } from 'components/contexts/CurrentActor';
import CheckboxFilter from 'components/filters/CheckboxFilter';
import MultiSelectFilter from 'components/filters/MultiSelectFilter';
import SelectFilter from 'components/filters/SelectFilter';
import { allowanceTypes } from 'components/settings/users/hooks/useUserFilters';

export default function UsersFilterSidePanel({
  filterLabels,
  filters,
  isSidePanelOpen,
  onClose,
  onFilterAdd,
  onFilterRemove,
  onFilterValueAdd,
  onFilterValueChange,
  onFilterValueRemove,
  selectedFilters,
}) {
  const isInitialMount = useRef(true);

  const { t: i18nT } = useTranslation('components', {
    keyPrefix: 'settings.users.UsersFilterSidePanel',
  });

  const { t: i18nTShared } = useTranslation('common', {
    keyPrefix: 'shared',
  });

  const isAddFilterDisabled = selectedFilters.length === filterLabels.size;

  const headerContent = (
    <div className='tw-flex tw-items-center tw-justify-between'>
      <h1 className='tw-m-0 tw-text-xl tw-font-semibold tw-tracking-tight tw-text-grey-900'>
        {i18nT('title')}
      </h1>
      <button
        className='tw-h-7 tw-w-7 tw-rounded-lg tw-border-0 tw-bg-white tw-p-0 [&_polygon]:tw-fill-grey-500'
        onClick={onClose}
        type='button'
      >
        <CloseIcon height={28} width={28} />
        <span className='tw-sr-only'>{i18nTShared('close')}</span>
      </button>
    </div>
  );

  const autoFocus = !isInitialMount.current;

  const filterComponents = {
    allowanceTypeFilter: () => (
      <AllowanceTypeFilter
        autoFocus={autoFocus}
        filter={filters.allowanceTypeFilter}
        onFilterRemove={onFilterRemove}
        onFilterValueChange={onFilterValueChange}
      />
    ),
    divisionFilter: () => (
      <DivisionFilter
        autoFocus={autoFocus}
        filter={filters.divisionFilter}
        onFilterRemove={onFilterRemove}
        onFilterValueAdd={onFilterValueAdd}
        onFilterValueRemove={onFilterValueRemove}
      />
    ),
  };

  const bodyContent = (
    <>
      {selectedFilters.map((filterKey) => (
        <div
          className='tw-border-0 tw-border-b-1 tw-border-solid tw-border-grey-100 tw-p-5'
          key={filterKey}
        >
          {filterComponents[filterKey]()}
        </div>
      ))}
      <div className='tw-mb-28 tw-p-5'>
        <Options
          align='left'
          btnToggle={(isOpen) => (
            <div className='tooltip-parent'>
              <OutlinedButton
                color='grey'
                disabled={isAddFilterDisabled}
                onClick={() => {}}
                size='sm'
              >
                <span>{i18nTShared('addFilter')}</span>
                {isOpen ?
                  <ChevronUpIcon
                    className='-tw-mb-px -tw-mr-[6px]'
                    height={24}
                    width={24}
                  />
                : <ChevronDownIcon
                    className='-tw-mb-px -tw-mr-[6px]'
                    height={24}
                    width={24}
                  />
                }
              </OutlinedButton>
              {isAddFilterDisabled && (
                <Tooltip
                  placement='top'
                  tooltip={i18nTShared('allFiltersHaveBeenAdded')}
                  trigger='hover'
                />
              )}
            </div>
          )}
          buttonDisabled={isAddFilterDisabled}
          listStyle='tw-w-52 [&_li]:tw-border-none'
          toggleStyle='tw-inline-block'
        >
          {[...filterLabels].map(
            ([filterKey, label]) =>
              !selectedFilters.includes(filterKey) && (
                <OptionChoice
                  key={filterKey}
                  onClick={(setOptionsOpen) => {
                    setOptionsOpen(false);
                    onFilterAdd(filterKey);
                  }}
                >
                  <a className='tw-text-m tw-font-medium tw-tracking-auto tw-text-grey-700 hover:tw-text-grey-700'>
                    {label}
                  </a>
                </OptionChoice>
              ),
          )}
        </Options>
      </div>
    </>
  );

  useEffect(() => {
    isInitialMount.current = false;
  }, []);

  return (
    <SidePanel
      bodyContent={bodyContent}
      closeCallback={onClose}
      color='tertiary'
      displayClose={false}
      headerContent={headerContent}
      isOpen={isSidePanelOpen}
    />
  );
}

UsersFilterSidePanel.propTypes = {
  filterLabels: PropTypes.instanceOf(Map).isRequired,
  filters: PropTypes.shape({
    allowanceTypeFilter: PropTypes.objectOf(PropTypes.bool),
    divisionFilter: PropTypes.shape({
      selectedIds: PropTypes.arrayOf(PropTypes.string).isRequired,
    }),
  }).isRequired,
  isSidePanelOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onFilterAdd: PropTypes.func.isRequired,
  onFilterRemove: PropTypes.func.isRequired,
  onFilterValueAdd: PropTypes.func.isRequired,
  onFilterValueChange: PropTypes.func.isRequired,
  onFilterValueRemove: PropTypes.func.isRequired,
  selectedFilters: PropTypes.arrayOf(PropTypes.string).isRequired,
};

function AllowanceTypeFilter({
  autoFocus,
  filter,
  onFilterRemove,
  onFilterValueChange,
}) {
  const {
    division: {
      attributes: { singularRAMSName },
    },
  } = useCurrentActor();

  const { t: i18nT } = useTranslation('components', {
    keyPrefix: 'settings.users.UsersFilterSidePanel.filters.allowanceType',
  });

  const { t: i18nTTable } = useTranslation('components', {
    keyPrefix: 'settings.users.UserManagementTable',
  });

  const { t: i18nTShared } = useTranslation('common', {
    keyPrefix: 'shared',
  });

  const [isSelectable, setIsSelectable] = useState(
    !Object.values(filter).some(Boolean),
  );

  const label = i18nTShared('allowanceType');

  const options = allowanceTypes.map((allowanceType) => ({
    key: allowanceType,
    label: i18nTTable(`allowanceType.types.${allowanceType}`, {
      interpolation: { escapeValue: false },
      singularRAMSName: voca.capitalize(singularRAMSName),
    }),
  }));

  return isSelectable ?
      <SelectFilter
        autoFocus={autoFocus}
        id='allowance_type_filter'
        label={label}
        name='allowance_type_filter'
        onChange={(key) => {
          onFilterValueChange({
            filterKey: 'allowanceTypeFilter',
            key,
            value: true,
          });
          setIsSelectable(false);
        }}
        onRemove={() => onFilterRemove('allowanceTypeFilter')}
        options={options.map((option) => ({
          label: option.label,
          value: option.key,
        }))}
        placeholder={i18nT('placeholder')}
      />
    : <CheckboxFilter
        checkboxes={options.map((option) => ({
          label: option.label,
          name: `allowance_type_filter_${_.snakeCase(option.key)}`,
          onChange: (event) =>
            onFilterValueChange({
              filterKey: 'allowanceTypeFilter',
              key: option.key,
              value: event.target.checked,
            }),
          value: filter[option.key],
        }))}
        label={label}
        minChecked={0}
        onRemove={() => onFilterRemove('allowanceTypeFilter')}
      />;
}

AllowanceTypeFilter.propTypes = {
  autoFocus: PropTypes.bool.isRequired,
  filter: PropTypes.objectOf(PropTypes.bool).isRequired,
  onFilterRemove: PropTypes.func.isRequired,
  onFilterValueChange: PropTypes.func.isRequired,
};

function DivisionFilter({
  autoFocus,
  filter,
  onFilterRemove,
  onFilterValueAdd,
  onFilterValueRemove,
}) {
  const { t: i18nT } = useTranslation('components', {
    keyPrefix: 'settings.users.UsersFilterSidePanel.filters.divisions',
  });

  const { t: i18nTShared } = useTranslation('common', {
    keyPrefix: 'shared',
  });

  return (
    <MultiSelectFilter
      autoFocus={autoFocus}
      id='division_filter_selected_ids'
      label={i18nTShared('divisionsParentheses')}
      name='division_filter_selected_ids'
      onDeselect={(value) =>
        onFilterValueRemove({
          filterKey: 'divisionFilter',
          key: 'selectedIds',
          value,
        })
      }
      onRemove={() => onFilterRemove('divisionFilter')}
      onSelect={(value) =>
        onFilterValueAdd({
          filterKey: 'divisionFilter',
          key: 'selectedIds',
          value,
        })
      }
      placeholder={i18nT('placeholder')}
      resourceLabel={(resource) => resource.attributes.name}
      resourceSort={sortDivisions}
      selectedIds={filter.selectedIds}
      type='division'
    />
  );
}

DivisionFilter.propTypes = {
  autoFocus: PropTypes.bool.isRequired,
  filter: PropTypes.shape({
    selectedIds: PropTypes.arrayOf(PropTypes.string).isRequired,
  }).isRequired,
  onFilterRemove: PropTypes.func.isRequired,
  onFilterValueAdd: PropTypes.func.isRequired,
  onFilterValueRemove: PropTypes.func.isRequired,
};

function sortDivisions(a, b) {
  if (a.record.attributes.primary) return -1;
  if (b.record.attributes.primary) return 1;

  return _.lowerCase(a.label) > _.lowerCase(b.label) ? 1 : -1;
}
